import { useState, useEffect } from 'react';
import { Tooltip, Popconfirm, message, InputNumber, Button, Modal, Input, Select, DatePicker } from 'antd';
import { SaveOutlined, PlusOutlined } from '@ant-design/icons';
// Extraer Option de Select
const { Option } = Select;
// Datos de ejemplo (en un proyecto real estos vendrían de una API o archivos JSON)
const rubrosDB = [
  {
    id: 1,
    codigo: '01.01.01',
    nombre: 'Excavación manual en tierra',
    unidad: 'm³',
    materiales: [],
    manoObra: [
      { 
        id: 'mo-201',
        descripcion: 'Peón excavador', 
        unidad: 'hora', 
        cantidad: 1.5, 
        pu: 4.80, 
        rendimiento: 0.67, 
        subtotal: 7.20, 
        porcentaje: '25.26%',
      }
    ],
    herramientas: [],
    maquinaria: [],
    transporte: [],
    rendimiento: {
      dia: '7.0 m³/día',
      hora: '0.88 m³/hora',
      unidad: '1.14 horas/m³'
    },
    total: 7.20,
    type: 'excavation'
  }
];

const materialesDB = [
  {
    id: 'mat-101',
    descripcion: 'Cemento Portland Tipo I',
    unidad: 'bolsa',
    precios: [{ precio: 28.00, fecha: '2023-01-01' }],
    type: ['concrete', 'walls']
  }
];

const manoObraDB = [
  {
    id: 'mo-201',
    descripcion: 'Peón excavador',
    unidad: 'hora',
    precios: [{ precio: 4.80, fecha: '2023-01-01' }],
    type: ['excavation', 'general']
  }
];

const herramientasDB = [];
const maquinariaDB = [];
const transporteDB = [];

const Apus = () => {
  // States principales
  const [rubros, setRubros] = useState([]);
  const [selectedRubro, setSelectedRubro] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rightPanelContent, setRightPanelContent] = useState('menu');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [showRightMenu, setShowRightMenu] = useState(true);
  const [activeTableCategory, setActiveTableCategory] = useState('materiales');
  const [showFullAPU, setShowFullAPU] = useState(false);
  const [fullCategoryData, setFullCategoryData] = useState({
    materiales: [],
    manoObra: [],
    herramientas: [],
    maquinaria: [],
    transporte: []
  });
  const [draggedItem, setDraggedItem] = useState(null);
  
  // States para modales
  const [showAddRubroModal, setShowAddRubroModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newRubro, setNewRubro] = useState({
    codigo: '',
    nombre: '',
    unidad: '',
    type: 'general',
    rendimiento: { dia: '', hora: '', unidad: '' }
  });
  const [newItem, setNewItem] = useState({
    descripcion: '',
    unidad: '',
    precios: [{ precio: 0, fecha: new Date().toISOString().split('T')[0] }],
    type: 'general',
    rendimiento: ''
  });
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [fileName, setFileName] = useState('apu');

  // Load data
  useEffect(() => {
    setRubros(rubrosDB);
    setSelectedRubro(rubrosDB[0]);
    setFullCategoryData({
      materiales: materialesDB,
      manoObra: manoObraDB,
      herramientas: herramientasDB,
      maquinaria: maquinariaDB,
      transporte: transporteDB
    });
  }, []);

  // Filtros
  const filteredRubros = rubros.filter(rubro =>
    `${rubro.codigo} ${rubro.nombre}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Funciones de utilidad
  const isItemInSelectedRubro = (category, itemId) => {
    if (!selectedRubro || !selectedRubro[category]) return false;
    return selectedRubro[category].some(item => item.id === itemId);
  };

  const calculateRubroTotal = (rubro) => {
    let total = 0;
    ['materiales', 'manoObra', 'herramientas', 'maquinaria', 'transporte'].forEach(category => {
      if (rubro[category]) {
        rubro[category].forEach(item => {
          if (item.subtotal) {
            total += parseFloat(item.subtotal);
          }
        });
      }
    });
    return parseFloat(total.toFixed(2));
  };

  const updateRubroTotal = (rubro) => {
    const total = calculateRubroTotal(rubro);
    return { ...rubro, total };
  };

  // Funciones de arrastrar y soltar
  const handleDragStart = (e, item, category) => {
    setDraggedItem({ ...item, category });
    e.dataTransfer.setData('text/plain', JSON.stringify({ ...item, category }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (!data) return;

    const { category, ...item } = JSON.parse(data);
    
    if (isItemInSelectedRubro(category, item.id)) {
      message.warning('Este ítem ya está en el rubro');
      return;
    }

    const updatedRubro = { ...selectedRubro };
    if (!updatedRubro[category]) {
      updatedRubro[category] = [];
    }

    const lastPrice = item.precios[item.precios.length - 1].precio;
    const newItem = {
      ...item,
      cantidad: 1,
      pu: lastPrice,
      subtotal: lastPrice,
      porcentaje: '0%'
    };

    updatedRubro[category] = [...updatedRubro[category], newItem];
    const rubroWithUpdatedTotal = updateRubroTotal(updatedRubro);
    
    setSelectedRubro(rubroWithUpdatedTotal);
    setRubros(rubros.map(r => r.id === rubroWithUpdatedTotal.id ? rubroWithUpdatedTotal : r));
    message.success('Ítem agregado al rubro');
  };

  const handleDragOver = (e) => e.preventDefault();

  // Funciones para eliminar y actualizar items
  const handleDeleteItem = (category, itemId) => {
    const updatedRubro = { ...selectedRubro };
    updatedRubro[category] = updatedRubro[category].filter(item => item.id !== itemId);
    const rubroWithUpdatedTotal = updateRubroTotal(updatedRubro);
    
    setSelectedRubro(rubroWithUpdatedTotal);
    setRubros(rubros.map(r => r.id === rubroWithUpdatedTotal.id ? rubroWithUpdatedTotal : r));
    message.success('Ítem eliminado del rubro');
  };

  const handleUpdateQuantity = (category, itemId, value) => {
    const updatedRubro = { ...selectedRubro };
    const itemIndex = updatedRubro[category].findIndex(item => item.id === itemId);
    
    if (itemIndex !== -1) {
      updatedRubro[category][itemIndex] = {
        ...updatedRubro[category][itemIndex],
        cantidad: value,
        subtotal: value * updatedRubro[category][itemIndex].pu
      };
      
      const rubroWithUpdatedTotal = updateRubroTotal(updatedRubro);
      setSelectedRubro(rubroWithUpdatedTotal);
      setRubros(rubros.map(r => r.id === rubroWithUpdatedTotal.id ? rubroWithUpdatedTotal : r));
    }
  };

  const handleUpdateUnitPrice = (category, itemId, value) => {
    const updatedRubro = { ...selectedRubro };
    const itemIndex = updatedRubro[category].findIndex(item => item.id === itemId);
    
    if (itemIndex !== -1) {
      updatedRubro[category][itemIndex] = {
        ...updatedRubro[category][itemIndex],
        pu: value,
        subtotal: value * updatedRubro[category][itemIndex].cantidad
      };
      
      const rubroWithUpdatedTotal = updateRubroTotal(updatedRubro);
      setSelectedRubro(rubroWithUpdatedTotal);
      setRubros(rubros.map(r => r.id === rubroWithUpdatedTotal.id ? rubroWithUpdatedTotal : r));
    }
  };

  // Funciones para modales
  const handleAddRubro = () => {
    const rubroWithId = { 
      ...newRubro, 
      id: rubros.length + 1,
      materiales: [],
      manoObra: [],
      herramientas: [],
      maquinaria: [],
      transporte: [],
      total: 0
    };
    
    const updatedRubros = [...rubros, rubroWithId];
    setRubros(updatedRubros);
    setSelectedRubro(rubroWithId);
    setShowAddRubroModal(false);
    setNewRubro({
      codigo: '',
      nombre: '',
      unidad: '',
      type: 'general',
      rendimiento: { dia: '', hora: '', unidad: '' }
    });
    message.success('Rubro creado exitosamente');
  };

  const handleAddItem = () => {
    const newItemWithId = {
      ...newItem,
      id: `${activeTableCategory}-${Date.now()}`,
      cantidad: 1,
      pu: newItem.precios[0].precio,
      subtotal: newItem.precios[0].precio,
      porcentaje: '0%'
    };

    // Agregar a la categoría correspondiente
    const updatedCategoryData = { ...fullCategoryData };
    updatedCategoryData[activeTableCategory] = [
      ...updatedCategoryData[activeTableCategory],
      newItemWithId
    ];
    setFullCategoryData(updatedCategoryData);

    // También agregar al rubro seleccionado si está en la vista de categoría
    if (rightPanelContent === activeTableCategory) {
      const updatedRubro = { ...selectedRubro };
      if (!updatedRubro[activeTableCategory]) {
        updatedRubro[activeTableCategory] = [];
      }
      updatedRubro[activeTableCategory] = [...updatedRubro[activeTableCategory], newItemWithId];
      const rubroWithUpdatedTotal = updateRubroTotal(updatedRubro);
      setSelectedRubro(rubroWithUpdatedTotal);
      setRubros(rubros.map(r => r.id === rubroWithUpdatedTotal.id ? rubroWithUpdatedTotal : r));
    }

    setShowAddItemModal(false);
    setNewItem({
      descripcion: '',
      unidad: '',
      precios: [{ precio: 0, fecha: new Date().toISOString().split('T')[0] }],
      type: selectedRubro?.type || 'general',
      rendimiento: ''
    });
    message.success('Ítem creado exitosamente');
  };

  // Funciones para guardar en JSON
  const exportToJson = () => {
    const dataToExport = {
      rubros,
      materiales: fullCategoryData.materiales,
      manoObra: fullCategoryData.manoObra,
      herramientas: fullCategoryData.herramientas,
      maquinaria: fullCategoryData.maquinaria,
      transporte: fullCategoryData.transporte,
      fechaExportacion: new Date().toISOString()
    };

    const jsonStr = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName || 'apu'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSaveModalVisible(false);
    message.success('Datos exportados exitosamente');
  };

  // Renderizado de filas de categoría
  const renderCategoryRows = (categoryData) => {
    if (!categoryData || categoryData.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="px-4 py-1 text-center text-gray-500">
            No hay datos disponibles
          </td>
        </tr>
      );
    }

    return categoryData
      .filter(item => item.descripcion.toLowerCase().includes(categorySearchTerm.toLowerCase()))
      .map(item => (
        <tr key={item.id} className="hover:bg-gray-50">
          <td className="px-4 py-1 text-left">{item.descripcion}</td>
          <td className="px-4 py-1 text-center">{item.unidad}</td>
          <td className="px-4 py-1 text-center">
            <InputNumber
              min={0}
              step={0.1}
              value={item.cantidad}
              onChange={(value) => handleUpdateQuantity(activeTableCategory, item.id, value)}
              style={{ width: '80px' }}
            />
          </td>
          <td className="px-4 py-1 text-right">
            <InputNumber
              min={0}
              step={0.01}
              value={item.pu}
              onChange={(value) => handleUpdateUnitPrice(activeTableCategory, item.id, value)}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              style={{ width: '100px' }}
            />
          </td>
          <td className="px-4 py-1 text-center">{item.rendimiento || '-'}</td>
          <td className="px-4 py-1 text-right">${item.subtotal?.toFixed(2)}</td>
          <td className="px-4 py-1 text-center">{item.porcentaje}</td>
          <td className="px-4 py-1 text-center">
            <Popconfirm
              title="¿Eliminar este ítem?"
              onConfirm={() => handleDeleteItem(activeTableCategory, item.id)}
              okText="Sí"
              cancelText="No"
            >
              <button className="text-red-500 hover:text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </Popconfirm>
          </td>
        </tr>
      ));
  };

  // Renderizado de tablas
  const renderFullAPUTable = () => {
    const categories = [
      { key: 'materiales', title: 'Materiales', data: selectedRubro.materiales },
      { key: 'manoObra', title: 'Mano de Obra', data: selectedRubro.manoObra },
      { key: 'herramientas', title: 'Herramientas', data: selectedRubro.herramientas },
      { key: 'maquinaria', title: 'Maquinaria', data: selectedRubro.maquinaria },
      { key: 'transporte', title: 'Transporte', data: selectedRubro.transporte }
    ];

    return (
      <div 
        className="bg-white shadow-md rounded-lg px-6 py-1 mb-1 overflow-x-auto"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">
              {selectedRubro.codigo} - {selectedRubro.nombre} (APU Completo)
            </h2>
            <p className="text-sm text-gray-600">{selectedRubro.unidad}</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="bg-blue-500 text-white px-3 py-1 rounded-md text-lg font-semibold">
              ${selectedRubro.total?.toFixed(2)}
            </div>
            <button
              onClick={() => setShowFullAPU(false)}
              className="p-2 rounded hover:bg-gray-200"
              title="Ver vista por categorías"
            >
              ✖ Cerrar vista completa
            </button>
          </div>
        </div>

        {categories.map(({ key, title, data }) => (
          <div key={key} className="mb-1">
            <h3 className="font-bold text-lg mb-3 bg-gray-100 px-4 py-2 rounded-t-lg flex items-center">
              <span className="mr-2">
                {key === 'materiales' && '📦'}
                {key === 'manoObra' && '👷'}
                {key === 'herramientas' && '🛠️'}
                {key === 'maquinaria' && '🚜'}
                {key === 'transporte' && '🚚'}
              </span>
              {title}
            </h3>
            <table className="min-w-full divide-y divide-gray-200 mb-4">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Unidad</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">P. Unitario</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rendimiento</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {renderCategoryRows(data)}
              </tbody>
            </table>
          </div>
        ))}

        <div className="mt-1 px-4 py-1 bg-gray-50 rounded-lg">
          <h4 className="font-bold mb-2">Rendimientos</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-semibold my-1">Por día:</p>
              <p className="m-0 py-1">{selectedRubro.rendimiento.dia}</p>
            </div>
            <div>
              <p className="font-semibold my-1">Por hora:</p>
              <p className="my-1">{selectedRubro.rendimiento.hora}</p>
            </div>
            <div>
              <p className="font-semibold my-1">Por unidad:</p>
              <p className="my-1">{selectedRubro.rendimiento.unidad}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryTable = () => {
    const tableCategories = {
      materiales: { title: 'Materiales', data: selectedRubro.materiales },
      manoObra: { title: 'Mano de Obra', data: selectedRubro.manoObra },
      herramientas: { title: 'Herramientas', data: selectedRubro.herramientas },
      maquinaria: { title: 'Maquinaria', data: selectedRubro.maquinaria },
      transporte: { title: 'Transporte', data: selectedRubro.transporte }
    };

    return (
      <div 
        className="bg-white shadow-md rounded-lg px-6 py-1 mb-1 overflow-x-auto"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div className="flex justify-between items-center mb-1">
          <div>
            <h2 className="text-xl font-bold">
              {selectedRubro.codigo} - {selectedRubro.nombre}
            </h2>
            <p className="text-sm text-gray-600">{selectedRubro.unidad}</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="bg-blue-500 text-white px-3 py-1 rounded-md text-lg font-semibold">
              ${selectedRubro.total?.toFixed(2)}
            </div>
            <button
              onClick={() => setShowFullAPU(true)}
              className="ml-2 px-3 py-1 bg-green-500 text-white rounded-md text-sm hover:bg-green-600 flex items-center"
              title="Ver APU completo"
            >
              <span className="mr-1">📊</span> Vista Completa
            </button>
            <button
              onClick={() => setSaveModalVisible(true)}
              className="ml-2 px-3 py-1 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 flex items-center"
              title="Guardar APU"
            >
              <SaveOutlined className="mr-1" /> Guardar
            </button>
          </div>
        </div>
        <div className="flex border-b mb-4">
          {Object.entries(tableCategories).map(([key, { title }]) => (
            <button
              key={key}
              onClick={() => {
                setActiveTableCategory(key);
                setRightPanelContent(key);
              }}
              className={`px-4 py-1 font-medium ${
                activeTableCategory === key
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {title}
            </button>
          ))}
        </div>

        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
              <th className="px-4 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Unidad</th>
              <th className="px-4 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
              <th className="px-4 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">P. Unitario</th>
              <th className="px-4 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rendimiento</th>
              <th className="px-4 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
              <th className="px-4 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
              <th className="px-4 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {renderCategoryRows(tableCategories[activeTableCategory]?.data)}
          </tbody>
        </table>

        <div className="mt-1 py-1 bg-gray-50 rounded-lg">
          <h4 className="font-bold mb-2">Rendimientos</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-semibold my-1">Por día:</p>
              <p className="my-1">{selectedRubro.rendimiento.dia}</p>
            </div>
            <div>
              <p className="font-semibold my-1">Por hora:</p>
              <p className="my-1">{selectedRubro.rendimiento.hora}</p>
            </div>
            <div>
              <p className="font-semibold my-1">Por unidad:</p>
              <p className="my-1">{selectedRubro.rendimiento.unidad}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRightPanelContent = () => {
    const categories = {
      materiales: { title: 'Materiales', icon: '📦' },
      manoObra: { title: 'Mano de Obra', icon: '👷' },
      herramientas: { title: 'Herramientas', icon: '🛠️' },
      maquinaria: { title: 'Maquinaria', icon: '🚜' },
      transporte: { title: 'Transporte', icon: '🚚' }
    };

    if (rightPanelContent === 'menu') {
      return (
        <div className="px-4 py-1">
          <h3 className="font-bold mb-3 text-lg">Categorías</h3>
          <div className="space-y-2">
            {Object.entries(categories).map(([key, { title, icon }]) => (
              <button
                key={key}
                onClick={() => {
                  setRightPanelContent(key);
                  setActiveTableCategory(key);
                  setCategorySearchTerm('');
                }}
                className={`w-full p-3 rounded-lg text-left flex items-center ${
                  activeTableCategory === key 
                    ? 'bg-blue-100 border border-blue-300' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <span className="text-xl mr-3">{icon}</span>
                {title}
              </button>
            ))}
          </div>
        </div>
      );
    }

    const currentCategory = categories[rightPanelContent];
    const allCategoryItems = fullCategoryData[rightPanelContent] || [];
    const selectedRubroItems = selectedRubro?.[rightPanelContent] || [];
    const rubroType = selectedRubro?.type || 'general';

    const filteredItems = allCategoryItems.filter(item => 
      item.type.includes(rubroType) || item.type.includes('all') || item.type === 'general'
    );

    return (
      <div className="px-4 py-1">
        <div className="flex items-center mb-1">
          <button 
            onClick={() => setRightPanelContent('menu')}
            className="mr-2 p-1 rounded hover:bg-gray-200"
          >
            ←
          </button>
          <h3 className="font-bold text-lg flex items-center">
            <span className="mr-2">{currentCategory.icon}</span>
            {currentCategory.title}
          </h3>
          <button
            onClick={() => setShowAddItemModal(true)}
            className="ml-auto p-1 text-blue-500 hover:text-blue-700"
            title={`Añadir ${currentCategory.title.toLowerCase()}`}
          >
            <PlusOutlined />
          </button>
        </div>
        
        <input
          type="text"
          placeholder={`Buscar ${currentCategory.title.toLowerCase()}...`}
          className="w-full p-2 mb-3 border rounded"
          value={categorySearchTerm}
          onChange={(e) => setCategorySearchTerm(e.target.value)}
        />
        
        <div className="space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto">
          {filteredItems.length > 0 ? (
            filteredItems.map(item => {
              const isInRubro = isItemInSelectedRubro(rightPanelContent, item.id);
              const lastPrice = item.precios[item.precios.length - 1]?.precio || 0;
              const unit = item.unidad || 'und';

              return (
                <div 
                  key={item.id} 
                  className={`p-3 rounded border cursor-pointer hover:bg-cyan-200 ${
                    isInRubro 
                      ? 'bg-blue-50 border-blue-300' 
                      : 'bg-white hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setActiveTableCategory(rightPanelContent);
                    setShowFullAPU(false);
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item, rightPanelContent)}
                >
                  <div className="flex justify-between items-start">
                    <p className="font-medium">{item.descripcion}</p>
                    {isInRubro && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded ml-2">
                        En rubro
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mt-1">
                    <div>
                      <span className="block">Unidad: {unit}</span>
                      <span>Precio: ${lastPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-4 text-gray-500">
              No hay {currentCategory.title.toLowerCase()} registrados
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAPUTable = () => {
    if (!selectedRubro) return (
      <div 
        className="bg-white shadow-md rounded-lg px-6 py-1 text-center"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <p className="text-gray-500">Seleccione un rubro para comenzar</p>
      </div>
    );

    return showFullAPU ? renderFullAPUTable() : renderCategoryTable();
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left panel - Rubros list */}
      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="px-4 py-1 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Lista de Rubros</h2>
            <button
              onClick={() => setShowAddRubroModal(true)}
              className="p-1 text-blue-500 hover:text-blue-700"
              title="Añadir nuevo rubro"
            >
              <PlusOutlined />
            </button>
          </div>
          <input
            type="text"
            placeholder="Buscar rubro..."
            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="divide-y divide-gray-200">
          {filteredRubros.map(rubro => (
            <div
              key={rubro.id}
              className={`p-3 hover:bg-gray-50 cursor-pointer ${
                selectedRubro?.id === rubro.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
              }`}
              onClick={() => {
                setSelectedRubro(rubro);
                setRightPanelContent('menu');
                setCategorySearchTerm('');
                setShowFullAPU(false);
              }}
            >
              <div className="text-sm font-medium text-gray-900">
                {rubro.codigo} - {rubro.nombre}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{rubro.unidad}</span>
                <span className="font-medium">${rubro.total?.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Central panel - APU table */}
      <div className="flex-1 overflow-y-auto p-6">
        {renderAPUTable()}
      </div>

      {/* Right panel - Dynamic menu */}
      {showRightMenu && (
        <div className="w-72 bg-white border-l border-gray-200 overflow-y-auto">
          {renderRightPanelContent()}
        </div>
      )}

      {/* Right button bar */}
      <div className="flex flex-col items-center py-2 bg-gray-100 border-l w-12 overflow-y-auto space-y-3">
        {Object.entries({
          materiales: '📦',
          manoObra: '👷',
          herramientas: '🛠️',
          maquinaria: '🚜',
          transporte: '🚚'
        }).map(([key, icon]) => (
          <Tooltip key={key} placement="left" title={key.charAt(0).toUpperCase() + key.slice(1)}>
            <button
              onClick={() => {
                setRightPanelContent(key);
                setActiveTableCategory(key);
                setCategorySearchTerm('');
                setShowFullAPU(false);
              }}
              className={`w-9 h-9 rounded flex items-center justify-center text-lg ${
                activeTableCategory === key 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-200'
              }`}
            >
              {icon}
            </button>
          </Tooltip>
        ))}

        <div className="border-t border-gray-300 w-full my-1"></div>

        <Tooltip placement="left" title={showRightMenu ? "Ocultar menú" : "Mostrar menú"}>
          <button
            onClick={() => setShowRightMenu(!showRightMenu)}
            className="w-9 h-9 bg-white rounded flex items-center justify-center"
          >
            {showRightMenu ? '▶' : '◀'}
          </button>
        </Tooltip>
      </div>

      {/* Modal para añadir rubro */}
      <Modal
        title="Añadir Nuevo Rubro"
        visible={showAddRubroModal}
        onOk={handleAddRubro}
        onCancel={() => setShowAddRubroModal(false)}
        okText="Guardar"
        cancelText="Cancelar"
        width={600}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
              <Input
                name="codigo"
                value={newRubro.codigo}
                onChange={(e) => setNewRubro({...newRubro, codigo: e.target.value})}
                placeholder="Ej: 01.01.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Rubro</label>
              <Select
                className="w-full"
                value={newRubro.type}
                onChange={(value) => setNewRubro({...newRubro, type: value})}
              >
                <Option value="excavation">Excavación</Option>
                <Option value="concrete">Concreto</Option>
                <Option value="rebar">Acero de refuerzo</Option>
                <Option value="walls">Muros</Option>
                <Option value="finishes">Acabados</Option>
                <Option value="general">General</Option>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <Input
              name="nombre"
              value={newRubro.nombre}
              onChange={(e) => setNewRubro({...newRubro, nombre: e.target.value})}
              placeholder="Ej: Excavación manual en tierra"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
            <Input
              name="unidad"
              value={newRubro.unidad}
              onChange={(e) => setNewRubro({...newRubro, unidad: e.target.value})}
              placeholder="Ej: m³, kg, m²"
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="font-bold mb-3">Rendimientos</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Por día</label>
                <Input
                  name="dia"
                  value={newRubro.rendimiento.dia}
                  onChange={(e) => setNewRubro({
                    ...newRubro, 
                    rendimiento: {...newRubro.rendimiento, dia: e.target.value}
                  })}
                  placeholder="Ej: 7.0 m³/día"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Por hora</label>
                <Input
                  name="hora"
                  value={newRubro.rendimiento.hora}
                  onChange={(e) => setNewRubro({
                    ...newRubro, 
                    rendimiento: {...newRubro.rendimiento, hora: e.target.value}
                  })}
                  placeholder="Ej: 0.88 m³/hora"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Por unidad</label>
                <Input
                  name="unidad"
                  value={newRubro.rendimiento.unidad}
                  onChange={(e) => setNewRubro({
                    ...newRubro, 
                    rendimiento: {...newRubro.rendimiento, unidad: e.target.value}
                  })}
                  placeholder="Ej: 1.14 horas/m³"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal para añadir ítem */}
      <Modal
        title={`Añadir Nuevo ${rightPanelContent.charAt(0).toUpperCase() + rightPanelContent.slice(1)}`}
        visible={showAddItemModal}
        onOk={handleAddItem}
        onCancel={() => setShowAddItemModal(false)}
        okText="Guardar"
        cancelText="Cancelar"
        width={600}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <Input.TextArea
              name="descripcion"
              value={newItem.descripcion}
              onChange={(e) => setNewItem({...newItem, descripcion: e.target.value})}
              rows={3}
              placeholder="Descripción del item"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
              <Select
                className="w-full"
                value={newItem.unidad}
                onChange={(value) => setNewItem({...newItem, unidad: value})}
                placeholder="Seleccione unidad"
                showSearch
              >
                {rightPanelContent === 'materiales' && (
                  <>
                    <Option value="m³">m³</Option>
                    <Option value="kg">kg</Option>
                    <Option value="unidad">unidad</Option>
                    <Option value="bolsa">bolsa</Option>
                    <Option value="gal">gal</Option>
                  </>
                )}
                {rightPanelContent === 'manoObra' && (
                  <>
                    <Option value="hora">hora</Option>
                    <Option value="jornal">jornal</Option>
                    <Option value="dia">día</Option>
                  </>
                )}
                {rightPanelContent === 'herramientas' && (
                  <>
                    <Option value="%">%</Option>
                    <Option value="hora">hora</Option>
                    <Option value="dia">día</Option>
                  </>
                )}
                {rightPanelContent === 'maquinaria' && (
                  <>
                    <Option value="hora">hora</Option>
                    <Option value="dia">día</Option>
                    <Option value="semana">semana</Option>
                    <Option value="mes">mes</Option>
                  </>
                )}
                {rightPanelContent === 'transporte' && (
                  <>
                    <Option value="viaje">viaje</Option>
                    <Option value="km">km</Option>
                    <Option value="hora">hora</Option>
                  </>
                )}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio Unitario</label>
              <InputNumber
                className="w-full"
                min={0}
                step={0.01}
                value={newItem.precios[0].precio}
                onChange={(value) => setNewItem({
                  ...newItem,
                  precios: [{ precio: value, fecha: new Date().toISOString().split('T')[0] }]
                })}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
              />
            </div>
          </div>

          {(rightPanelContent === 'manoObra' || rightPanelContent === 'maquinaria') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rendimiento</label>
              <Input
                name="rendimiento"
                value={newItem.rendimiento}
                onChange={(e) => setNewItem({...newItem, rendimiento: e.target.value})}
                placeholder="Ej: 0.5 (indica cuántas unidades produce por hora)"
              />
            </div>
          )}

          <div className="text-xs text-gray-500">
            <p>Este ítem será disponible para rubros de tipo: {newItem.type}</p>
            <p>Fecha de registro: {newItem.precios[0].fecha}</p>
          </div>
        </div>
      </Modal>

      {/* Modal para guardar */}
      <Modal
        title="Guardar APU"
        visible={saveModalVisible}
        onOk={exportToJson}
        onCancel={() => setSaveModalVisible(false)}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del archivo</label>
            <Input
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Nombre del archivo"
              addonAfter=".json"
            />
          </div>
          <p className="text-sm text-gray-500">
            Se guardarán todos los rubros y sus componentes en un archivo JSON descargable.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Apus;